/**
 * Horizontal image row used by the fa-pdp-cards section.
 *
 * Scrolling itself is native CSS (overflow and scroll-snap), so the row works without this
 * script. The script only adds what CSS cannot: previous/next buttons that appear when the
 * row actually overflows, and keyboard focus on the track only when there is something to
 * scroll, so a fully visible row is not an empty tab stop.
 */
class FaPdpScrollRow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.arrows = this.querySelector('[data-arrows]');
    if (!this.track) return;

    this.buttons = this.arrows ? [...this.arrows.querySelectorAll('[data-step]')] : [];
    this.buttons.forEach((button) => button.addEventListener('click', this.#onArrow));
    this.track.addEventListener('scroll', this.#update, { passive: true });

    this.resizeObserver = new ResizeObserver(this.#update);
    this.resizeObserver.observe(this.track);
    this.#update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.track?.removeEventListener('scroll', this.#update);
    this.buttons?.forEach((button) => button.removeEventListener('click', this.#onArrow));
  }

  #onArrow = (event) => {
    const step = Number(event.currentTarget.dataset.step) || 1;
    const item = this.track.querySelector('li');
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const distance = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: step * distance, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  #update = () => {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const overflows = scrollWidth > clientWidth + 1;

    this.toggleAttribute('data-overflow', overflows);
    if (overflows) {
      this.track.setAttribute('tabindex', '0');
    } else {
      this.track.removeAttribute('tabindex');
    }

    if (!this.arrows) return;
    this.arrows.hidden = !overflows;
    const [previous, next] = this.buttons;
    if (previous) previous.disabled = scrollLeft <= 1;
    if (next) next.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
  };
}

if (!customElements.get('fa-pdp-scroll-row')) {
  customElements.define('fa-pdp-scroll-row', FaPdpScrollRow);
}
